import * as THREE from 'three';
import {UserData} from 'components/customer/RoomPlanner/types';

/**
 * Offset constants used in wall distance calculations.
 * Single source of truth — used by DistanceEditor (UI) and
 * createRoomObjects (blue measurement arrows).
 */
export const WALL_OFFSETS = {
    primary: 35,
    edge: 12.4,
    edgeAlt: 12.5,
    edgeAlt2: 12.6,
    back: 12.8,
    frontRect: 16.2,
    noDoorThickness: -0.6,
    lShapedBulkhead: 3.8,
    backLeftDir: 34.6,
    backRightDir: 35.4,
    rightAlt: 38.6,
    backAlt: 8.8,
    leftFrontGap: 31.3,
    rightFrontGap: 38.7,
};

export interface WallDistances {
    left: number;
    right: number;
    front: number;
    back: number;
}

export interface RoomConfig {
    roomType: string;
    rectangularDimension: {
        width: number;
        length: number;
        height: number;
    };
    lShapedDimension: {
        rightWidth: number;
        leftWidth: number;
        rightDepth: number;
        leftDepth: number;
        height: number;
    };
    scale: number;
}

/**
 * Internal context built from the cabinet group and room config.
 * Shared between forward (distance) and inverse (position) calculations.
 */
export interface CabinetWallContext {
    width: number;
    depth: number;
    position: THREE.Vector3;
    currentDirection: string;
    roomWidth: number;
    roomLength: number;
    effectiveRoomLength: number;
    effectiveRoomWidth: number;
    rightDepth: number;
    doorThickness: number;
    scale: number;
    roomType: string;
    isLShapedProduct: boolean;
    cabinetHasDoors: boolean;
    cabinetGroup: THREE.Group;
    lShapedDimension: {
        rightWidth: number;
        leftWidth: number;
        rightDepth: number;
        leftDepth: number;
        height: number;
    };
}

/**
 * Wall reference IDs used across the measurement system.
 * Rectangular rooms use: backWall, frontWall, leftWall, rightWall
 * L-shaped rooms use: leftWidthWall, rightWidthWall, leftDepthWall,
 *   rightDepthWall, leftWidth2Wall, rightWidth2Wall
 */
export type WallRefId =
    | 'backWall'
    | 'frontWall'
    | 'leftWall'
    | 'rightWall'
    | 'leftWidthWall'
    | 'rightWidthWall'
    | 'leftDepthWall'
    | 'rightDepthWall'
    | 'leftWidth2Wall'
    | 'rightWidth2Wall';

export interface WallMeasurements {
    cabinetMeasurements: THREE.Group<THREE.Object3DEventMap> | null;
    emptySpaceMeasurements?: THREE.Group<THREE.Object3DEventMap> | null;
}

export interface RectangularMeasurementGroups {
    backWall: WallMeasurements;
    frontWall: WallMeasurements | null;
    leftWall: WallMeasurements | null;
    rightWall: WallMeasurements | null;
}

export interface LShapedMeasurementGroups {
    leftWidthWall: WallMeasurements;
    leftWidth2Wall: WallMeasurements | null;
    rightWidthWall: WallMeasurements;
    rightWidth2Wall: WallMeasurements | null;
    leftDepthWall: WallMeasurements | null;
    rightDepthWall: WallMeasurements | null;
}

export type WithOptionalUserData<T extends THREE.Object3D, U> = T & {
    userData: T['userData'] & Partial<U>;
};

/**
 * Standardized measurement data structure for consistent measurement generation
 */
export interface MeasurementData {
    /** Start position of measurement */
    start: number;
    /** End position of measurement */
    end: number;
    /** Color of the measurement arrows and text */
    colour: string;
    /** Label text to display */
    label: string;
    /** Measurement type for styling and behavior */
    type: 'cabinet' | 'emptySpace' | 'wall';
    /** Unique identifier for the measurement */
    id: string;
    /** Y position for the measurement */
    yPosition: number;
    /** Z position for the measurement */
    zPosition: number;
    /** Cabinet ID if this is a cabinet measurement */
    cabinetId?: string;
    /** Whether to show guidelines */
    showGuidelines?: boolean;
    /** Priority for collision resolution (higher = more important) */
    priority?: number;
    /** Pre-calculated X position for arrows (room coordinate system) */
    xPosition?: number;
    /** Actual X position for arrows */
    actualPostionX?: number;
    /** Pre-calculated width for arrows */
    width?: number;
    /** Actual width for arrows */
    actualWidth?: number;
    /** Door width for cabinet */
    doorWidth?: number;
    actualSize?: {
        start: number;
        end: number;
        width: number;
        depth: number;
        height: number;
    };
    currentDirection?: string;
    compactOnOverlap?: boolean;
    /** Combined span label for an overlap cluster */
    compactMixedMeasurement?: boolean;
    /** Original cabinet label shown on its segment within an overlap cluster */
    compactSegmentLabel?: boolean;
    /** X positions for internal partition ticks (overlap junctions only) */
    compactInternalBoundaries?: number[];
    compactLabelScaledWidth?: number;
    cabinet?: WithOptionalUserData<
        THREE.Group<THREE.Object3DEventMap>,
        UserData
    >;
}

/**
 * Configuration for measurement generation
 */
export interface MeasurementConfig {
    wallHeight: number;
    defaultLabelFontSize: string;
    roomWidth?: number;
    roomLength?: number;
    leftWidth?: number;
    rightWidth?: number;
    leftDepth?: number;
    rightDepth?: number;
    scale?: number;
    wallRefId?: string;
    showArrows: boolean;
    isForPrinting?: boolean;
    previewPosition?: string;
}

export const getActualMeasurementExtents = (
    measurement: MeasurementData
): {start: number; end: number; width: number} => {
    const rawStart = measurement.actualSize?.start ?? measurement.start;
    const rawEnd = measurement.actualSize?.end ?? measurement.end;
    const start = rawStart;
    const end = rawEnd;

    return {
        start,
        end,
        width: end - start,
    };
};
